// Public invite-acceptance page — the tester sets their OWN credentials here.
import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Sparkles } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";

export default function InviteAccept() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { adoptSession } = useAuth();
  const [checking, setChecking] = useState(true);
  const [invalid, setInvalid] = useState("");
  const [invitedBy, setInvitedBy] = useState("");
  const [form, setForm] = useState({ name: "", clinic_name: "", email: "", password: "", confirm: "", ack: false });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/invites/${token}/check`)
      .then((r) => {
        setInvitedBy(r.data.invited_by);
        setForm((f) => ({
          ...f,
          name: r.data.assigned_name || f.name,
          email: r.data.assigned_email || f.email,
        }));
      })
      .catch((e) => setInvalid(e.response?.data?.detail || "This invite link is invalid."))
      .finally(() => setChecking(false));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 6) return toast.error("Password must be at least 6 characters");
    if (form.password !== form.confirm) return toast.error("Passwords don't match");
    if (!form.ack) return toast.error("Please acknowledge the disclaimer to continue");
    setBusy(true);
    try {
      const r = await api.post(`/invites/${token}/accept`, {
        name: form.name,
        email: form.email,
        password: form.password,
        clinic_name: form.clinic_name || null,
        disclaimer_acknowledged: true,
      });
      adoptSession(r.data.token, r.data.user);
      localStorage.setItem("pstr_show_tour", "1");
      toast.success("Welcome to Posturra!");
      navigate("/");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not create your account");
    } finally {
      setBusy(false);
    }
  };

  if (checking) {
    return <div className="min-h-screen bg-paper-50 flex items-center justify-center text-ink-500 text-sm" data-testid="invite-loading">Checking your invite…</div>;
  }
  if (invalid) {
    return (
      <div className="min-h-screen bg-paper-50 flex items-center justify-center px-6">
        <div className="max-w-md text-center" data-testid="invite-invalid">
          <div className="label-overline text-terracotta-600 mb-2">Invite unavailable</div>
          <h1 className="font-heading text-2xl text-ink-900 mb-2">This link can&rsquo;t be used</h1>
          <p className="text-ink-700 mb-6">{invalid}</p>
          <Link to="/login" className="underline underline-offset-4 text-ink-700">Go to sign in</Link>
        </div>
      </div>
    );
  }

  const inputCls = "w-full min-h-[48px] px-4 rounded-2xl bg-white border border-paper-200 focus:ring-2 focus:ring-sage-500/30 focus:border-sage-500 outline-none text-ink-900";
  return (
    <div className="min-h-screen bg-paper-50 flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-md" data-testid="invite-accept-page">
        <div className="text-center mb-8">
          <span className="w-12 h-12 rounded-2xl bg-sage-100 text-sage-700 inline-flex items-center justify-center mb-4">
            <Sparkles className="w-5 h-5" strokeWidth={1.5} />
          </span>
          <h1 className="font-heading text-3xl text-ink-900 mb-2">You&rsquo;re invited to Posturra</h1>
          <p className="text-ink-500 text-sm">{invitedBy} invited you to try Posturra. Set up your own account below — you choose your email and password.</p>
        </div>
        <form onSubmit={submit} className="space-y-3 bg-white border border-paper-200 rounded-3xl p-6 sm:p-8">
          <input data-testid="invite-name" required placeholder="Your full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} />
          <input data-testid="invite-clinic" placeholder="Clinic / practice name (optional)" value={form.clinic_name} onChange={(e) => setForm({ ...form, clinic_name: e.target.value })} className={inputCls} />
          <input data-testid="invite-email" required type="email" placeholder="Your email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} />
          <input data-testid="invite-password" required type="password" placeholder="Choose a password (min 6 characters)" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className={inputCls} />
          <input data-testid="invite-confirm" required type="password" placeholder="Confirm password" value={form.confirm} onChange={(e) => setForm({ ...form, confirm: e.target.value })} className={inputCls} />
          <label className="flex items-start gap-2.5 text-xs text-ink-700 cursor-pointer pt-1">
            <input data-testid="invite-ack" type="checkbox" checked={form.ack} onChange={(e) => setForm({ ...form, ack: e.target.checked })} className="w-4 h-4 mt-0.5 rounded border-paper-200 text-sage-500" />
            <span>I understand Posturra provides educational guidance for licensed professionals and is not a medical device.</span>
          </label>
          <button data-testid="invite-submit" type="submit" disabled={busy} className="w-full min-h-[52px] rounded-full bg-sage-500 hover:bg-sage-600 disabled:opacity-50 text-white font-medium transition-all">
            {busy ? "Creating your account…" : "Create my account"}
          </button>
        </form>
      </div>
    </div>
  );
}
